import { useState } from 'react';
import { EventEmitter } from '../events/EventEmitter';
import { useMount } from '../hooks';

export type THistoryCount = {
  currentStep: number;
  stepCount: number;
};

export type THistoryStep<T> = {
  content: T;
} & THistoryCount;

export type THistoryStepChange<T> = THistoryCount &
  THistoryStep<T> & {
    fromPush?: boolean;
    hasPrevious: boolean;
    hasNext: boolean;
  };

export type THistoryConfiguration<T> = {
  initialContent?: T;
  maxLength: number;
};

export type THistorySnapshot<T> = {
  content: T[];
  currentStep: number;
};

/**
 * # Cómo usar el historial
 *
 * Es muy sencillo: se instancia con un callback que recibirá un método push
 * cada vez que una ventana se cierre. En esos momentos, el usuario del History
 * puede hacer push al estado.
 *
 * ## La mecánica
 *
 * - Instancio la clase y recibo el método push
 * - Cuando se realizan cambios significativos, llamo al método openWindow
 * - El método openWindow da una ventana de 350ms antes de llamar al callback
 * - Si durante esa ventana se vuelve a llamar a openWindow, la ventana se
 * resetea
 * - Si al llamar al callback, se hace push al estado, ese estado es propagado
 * mediante el hook y mediante el evento on('stepChange')
 * 
 * ## Cómo escuchar cambios
 * 
 * ```ts
 * this.history.on('stepChange', (data) => {
     if (!data.fromPush) this.update(data.content);
  });
  ```
 *
 * ## Back y forward
 *
 * El back y forward son dos métodos del history que hacen un stepChange y
 * emiten a través de on('stepChange') y el hook
 */
export class History<T> extends EventEmitter<{
  stepChange: THistoryStepChange<T>;
}> {
  private autocloseTimeout = -1;
  private currentStep = 0;
  private content: T[] = [];
  private emitting = 0;
  protected enabled = true;
  private _isOpen = false;
  private timeoutMs = 350;
  private configuration: THistoryConfiguration<T>;

  constructor(
    onCloseWindow: (push: (newState: T) => void) => unknown,
    configuration?: Partial<THistoryConfiguration<T>>,
  ) {
    super();

    this.configuration = Object.assign({ maxLength: 15 }, configuration);
    if (configuration?.initialContent !== undefined) {
      this.content.push(configuration?.initialContent ?? (null as any));
    } else {
      this.currentStep = -1;
    }

    this.onCloseWindow = onCloseWindow;
  }

  private closeWindow = () => {
    if (this._isOpen) {
      this.onCloseWindow(this.push.bind(this));
      this._isOpen = false;
    }
  };

  private getEvent = () => {
    const stepCount = this.content.length - 1;
    const event = {
      content: this.content[this.currentStep],
      currentStep: this.currentStep,
      stepCount,
      hasNext: this.currentStep < stepCount,
      hasPrevious: this.currentStep > 0,
    };

    return event;
  };

  private onCloseWindow: (push: (newState: T) => void) => unknown;

  private push = (el: T) => {
    this.content = [...this.content.slice(0, this.currentStep + 1), el];
    this.content = this.content.slice(
      Math.max(
        0,
        this.content.length - (this.configuration?.maxLength ?? Infinity),
      ),
    );
    this.currentStep = this.content.length - 1;
    this.shoutStepChange(true);
  };

  private shoutStepChange = (fromPush?: boolean) => {
    try {
      this.emit('stepChange', { ...this.getEvent(), fromPush });
    } catch (e) {
      console.error(e);
    }
  };

  hooks = {
    useCountChange: (onChange: (ev: THistoryStepChange<T>) => unknown) => {
      useMount(() => {
        onChange(this.getEvent());

        this.on('stepChange', () => {
          onChange(this.getEvent());
        });
      });
    },
  };

  back = () => {
    clearTimeout(this.autocloseTimeout);
    if (this.currentStep > 0) {
      this.currentStep -= 1;
      this.shoutStepChange();
    }
  };

  disable = () => {
    this.enabled = false;
  };

  emit<K extends 'stepChange'>(
    eventName: K,
    params?: THistoryStepChange<T>,
  ): void {
    this.emitting++;

    try {
      super.emit(eventName, params as THistoryStepChange<T>);
    } finally {
      this.emitting--;
    }
  }

  enable = () => {
    this.enabled = true;
  };

  forward = () => {
    clearTimeout(this.autocloseTimeout);
    if (this.currentStep < this.content.length - 1) {
      this.currentStep += 1;
      this.shoutStepChange();
    }
  };

  getContent() {
    return this.content;
  }

  exportSnapshot = (): THistorySnapshot<T> => {
    this.closeWindow();
    return {
      content: [...this.content],
      currentStep: this.currentStep,
    };
  };

  importSnapshot = (snapshot?: Partial<THistorySnapshot<T>>) => {
    clearTimeout(this.autocloseTimeout);
    this._isOpen = false;

    const incomingContent = Array.isArray(snapshot?.content)
      ? [...snapshot.content]
      : [];

    if (incomingContent.length === 0) {
      if (this.configuration.initialContent !== undefined) {
        incomingContent.push(this.configuration.initialContent);
      } else {
        this.currentStep = -1;
      }
    }

    this.content = incomingContent;

    if (this.content.length > 0) {
      const lastStep = this.content.length - 1;
      const requestedStep =
        typeof snapshot?.currentStep === 'number'
          ? snapshot.currentStep
          : lastStep;
      this.currentStep = Math.max(0, Math.min(requestedStep, lastStep));
    }

    this.shoutStepChange();
  };

  get isOpen() {
    return this._isOpen;
  }

  openWindow = () => {
    if (!this.enabled || this.emitting !== 0) return;

    this._isOpen = true;

    clearTimeout(this.autocloseTimeout);
    this.autocloseTimeout = setTimeout(() => {
      this.closeWindow();
    }, this.timeoutMs) as unknown as number;
  };

  reset = () => {
    clearTimeout(this.autocloseTimeout);
    this.content = [];
    this.currentStep = 0;
    this.openWindow();
  };

  async runInWindow(cb: () => Promise<void>) {
    await cb();
    this.onCloseWindow(this.push.bind(this));
  }

  updateConfig = (newConfiguration: Partial<THistoryConfiguration<T>>) => {
    this.configuration = { ...this.configuration, ...newConfiguration };
  };

  useStep = () => {
    const [hasPrevious, setHasPrevious] = useState<
      Pick<THistoryStepChange<T>, 'hasNext' | 'hasPrevious' | 'currentStep'>
    >({ hasNext: false, hasPrevious: false, currentStep: this.currentStep });

    useMount(() => {
      return this.on('stepChange', (ev) => {
        setHasPrevious({
          currentStep: ev.currentStep,
          hasNext: ev.hasNext,
          hasPrevious: ev.hasPrevious,
        });
      });
    });

    return hasPrevious;
  };
}
